/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { FC, useCallback, useMemo, useState } from "react";
import {
	ISchemaCommentsModel,
	ISchemaConstArrayModel,
	SCHEMA_COMMENT_STATUS,
	TGenericModelNode,
	TSchemaComment,
	TSchemaCommentModel,
	ValidatorString
} from "@adapptio/lib-blueprint";
import { IInspectorControls, TInspectorControlComponent } from "../InspectorTypes";
import { useBlueprintModel } from "../../../hooks/useBlueprintModel";
import { InvalidModelNode } from "../InvalidModelNode";
import { FIELD_ROW_TYPE, FieldRow } from "../../Fields/FieldRow";
import { ACTION_BUTTON_SIZE, ACTION_BUTTON_STYLE, ActionButton } from "../../ActionButton";
import { FieldCard, FieldCardContent, FieldCardHeader, FieldCardText } from "../../Fields/FieldCard";
import { formatDateTimeISO } from "../../../../utils/dateTime";
import { isFunction } from "@adapptio/lib-shared";
import { IDropdownMenuItem } from "../../DropdownMenu";
import { useFieldState } from "../../../hooks/useFieldGroup";
import { FieldText } from "../../Fields/FieldText";
import { Form } from "../../Form";
import { ICON_BUTTON_SIZE, IconButton } from "../../IconButton";
import { BEM, SYSTEM_COLOR } from "@adapptio/lib-components";
import { FieldGroup } from "../../Fields/FieldGroup";
import { FieldSwitch } from "../../Fields/FieldSwitch";
import { FieldLabel } from "../../Fields/FieldLabel";
import { useUserInfo } from "../../../hooks/useUserInfo";
import { Markdown } from "../../Markdown";

export const ControlComments = (model: TGenericModelNode): IInspectorControls => {
	return {
		children: {
			component: ControlComments_Children,
			model: model
		}
	};
};

export const ControlComments_Children: TInspectorControlComponent<ISchemaCommentsModel> = ({
	model: _model,
	depth,
	path
}) => {
	const { model } = useBlueprintModel(_model, "comments");
	const userInfo = useUserInfo();

	const addMessage = useCallback(
		(value: string) => {
			model.schema.addMessage(
				model,
				{
					message: value,
					authorName: `${userInfo?.firstName} ${userInfo?.lastName}`,
					authorId: userInfo?.accountId,
					date: new Date()
				},
				true
			);
		},
		[ model ]
	);

	const acceptMessage = useCallback(
		(index: number) => {
			if (!model) {
				return;
			}

			model.schema.acceptMessage(model, index, true);
		},
		[ model ]
	);

	const declineMessage = useCallback(
		(index: number) => {
			if (!model) {
				return;
			}

			model.schema.declineMessage(model, index, true);
		},
		[ model ]
	);

	const removeMessage = useCallback(
		(index: number) => {
			if (!model) {
				return;
			}

			model.schema.removeMessage(model, index, true);
		},
		[ model ]
	);

	const unresolveMessage = useCallback(
		(index: number) => {
			if (!model) {
				return;
			}

			model.schema.unresolveMessage(model, index, true);
		},
		[ model ]
	);

	if (!model) {
		return <InvalidModelNode model={_model} />;
	}

	return (
		<>
			<ControlComments_AddItem model={model} onAdd={addMessage} />

			<ControlComments_Messages
				model={model.messages}
				depth={depth}
				path={path}
				onAcceptMessage={acceptMessage}
				onDeclineMessage={declineMessage}
				onRemoveMessage={removeMessage}
				onUnresolveMessage={unresolveMessage}
			/>
		</>
	);
};

interface ControlComments_AddItemProps {
	model: TGenericModelNode;
	onAdd: (value: string) => void;
}

const ControlComments_AddItem: FC<ControlComments_AddItemProps> = ({ model, onAdd }) => {
	const { updateRef } = useBlueprintModel(model);

	const baseClass = BEM("edt-inspector-control-comments-add", null, {});

	const [ fieldState, setFieldState ] = useFieldState(
		{
			message: {
				defaultValue: "",
				validators: [ ValidatorString({ required: true, min: 1 }) ]
			}
		},
		[ model, updateRef ]
	);

	const handleAdd = useCallback(() => {
		onAdd(fieldState.message.value);
		setFieldState.message("");
	}, [ onAdd, fieldState.message.value ]);

	return (
		<div className={baseClass.toClassName()}>
			<Form onSubmit={handleAdd}>
				<FieldRow>
					<FieldGroup>
						<FieldText
							grow
							value={fieldState.message.value}
							onChange={setFieldState.message}
							multiLine
							maxRows={3}
							placeholder="Add comment..."
						/>
					</FieldGroup>
				</FieldRow>

				<FieldRow>
					<ActionButton
						className={baseClass.element("submit-btn").toClassName()}
						onClick={handleAdd}
						label="Add Comment"
						type="submit"
						icon="mdi/comment-plus"
						size={ACTION_BUTTON_SIZE.SMALL}
						style={ACTION_BUTTON_STYLE.PRIMARY}
						disabled={!fieldState.message.value}
					/>
				</FieldRow>
			</Form>
		</div>
	);
};

interface ControlComments_MessagesProps {
	model: ISchemaConstArrayModel<TSchemaComment>;
	depth: number;
	path: string[];
	onAcceptMessage: (index: number) => void;
	onDeclineMessage: (index: number) => void;
	onRemoveMessage: (index: number) => void;
	onUnresolveMessage: (index: number) => void;
}

const ControlComments_Messages: React.FC<ControlComments_MessagesProps> = ({
	model: _model,
	depth,
	path,
	onAcceptMessage,
	onDeclineMessage,
	onRemoveMessage,
	onUnresolveMessage
}) => {
	const { model } = useBlueprintModel(_model);
	const baseClass = BEM("edt-inspector-control-comments-list", null, {});

	const [ showResolved, setShowResolved ] = useState(false);

	if (!model?.items?.length) {
		return null;
	}

	const items = showResolved
		? [ ...model.items ]
		: [ ...model.items ].filter((item) => item.props.status.value === SCHEMA_COMMENT_STATUS.UNRESOLVED);

	return (
		<div className={baseClass.toClassName()}>
			<FieldRow className={baseClass.element("filter").toClassName()}>
				<FieldSwitch value={showResolved} onChange={setShowResolved} />
				<FieldLabel>Show resolved comments</FieldLabel>
			</FieldRow>
			<FieldRow type={FIELD_ROW_TYPE.LIST} className={baseClass.element("items").toClassName()}>
				{items
					.map((item, index) => {
						return (
							<ControlComments_ChildItem
								key={item.nodeId}
								model={item}
								depth={depth}
								path={path.concat([ "item" ])}
								index={index}
								onAccept={onAcceptMessage}
								onDecline={onDeclineMessage}
								onRemove={onRemoveMessage}
								onReset={onUnresolveMessage}
							/>
						);
					})
					.reverse()}
			</FieldRow>
		</div>
	);
};

interface IControlComments_ChildItemProps {
	model: TSchemaCommentModel;
	depth: number;
	path: string[];
	index: number;
	onAccept: (index: number) => void;
	onDecline: (index: number) => void;
	onRemove: (index: number) => void;
	onReset: (index: number) => void;
}

const ControlComments_ChildItem: FC<IControlComments_ChildItemProps> = ({
	model: _model,
	index,
	path,
	onRemove,
	onAccept,
	onDecline,
	onReset
}) => {
	const { model } = useBlueprintModel(_model);

	const accepted = model.props.status.value === SCHEMA_COMMENT_STATUS.ACCEPTED;
	const declined = model.props.status.value === SCHEMA_COMMENT_STATUS.DECLINED;
	const resolved = accepted || declined;

	const baseClass = BEM("edt-inspector-control-comments-item", null, {
		status: model.props.status.value
	});

	const handleAccept = useCallback(() => {
		if (isFunction(onAccept)) {
			onAccept(index);
		}
	}, [ index, onAccept ]);

	const handleDecline = useCallback(() => {
		if (isFunction(onDecline)) {
			onDecline(index);
		}
	}, [ index, onDecline ]);

	const handleRemove = useCallback(() => {
		if (isFunction(onRemove)) {
			onRemove(index);
		}
	}, [ index, onRemove ]);

	const handleReset = useCallback(() => {
		if (isFunction(onReset)) {
			onReset(index);
		}
	}, [ index, onReset ]);

	const actionsMenuItems = useMemo<IDropdownMenuItem[]>(
		() => [
			{
				id: "unresolve",
				icon: "mdi/cancel",
				label: "Unresolve",
				onClick: handleReset,
				disabled: !resolved
			},
			{
				id: "remove",
				icon: "mdi/delete",
				label: "Remove",
				onClick: handleRemove
			}
		],
		[ handleRemove, resolved, handleReset ]
	);

	return (
		<FieldCard className={baseClass.toClassName()}>
			<FieldCardHeader
				title={model.props.authorName?.value}
				subheader={
					model.props.date?.value ? formatDateTimeISO(model.props.date.value.toISOString()) : ""
				}
				action={
					<>
						<IconButton
							icon="mdi/check"
							tooltip="Accept"
							color={SYSTEM_COLOR.SUCCESS}
							size={ICON_BUTTON_SIZE.XSMALL}
							onClick={handleAccept}
							active={accepted}
							disabled={resolved}
						/>
						<IconButton
							icon="mdi/close"
							tooltip="Decline"
							color={SYSTEM_COLOR.ERROR}
							size={ICON_BUTTON_SIZE.XSMALL}
							onClick={handleDecline}
							active={declined}
							disabled={resolved}
						/>
						<IconButton
							icon="mdi/dots-vertical"
							tooltip="Actions"
							size={ICON_BUTTON_SIZE.XSMALL}
							menuItems={actionsMenuItems}
							hideMenuArrow
						/>
					</>
				}
			/>
			<FieldCardContent>
				<FieldCardText>
					<Markdown>{model.props.message?.value}</Markdown>
				</FieldCardText>
			</FieldCardContent>
		</FieldCard>
	);
};
